import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Resets the window scroll when the route changes.
 *
 * React Router keeps window.scrollY across client-side navigations (only a full page load resets
 * it), so opening a work order from a scrolled list showed the middle of the details page instead
 * of its header. Rules:
 *
 * - pathname changes on a push/replace navigation → scroll to top
 * - back/forward (POP) → untouched, the browser restores its own saved position
 * - query-string-only change (tabs, filters, pagination, calendar date, ?highlight=) → untouched
 * - a hash in the URL → untouched, the anchor is the target
 * - navigate(to, { state: { preserveScroll: true } }) opts a single navigation out
 *
 * Rendered once, inside the router, from App.js.
 */
export default function ScrollToTop() {
  const { pathname, hash, state } = useLocation();
  const navigationType = useNavigationType();
  const lastPathname = useRef(pathname);

  // Layout effect: runs after the new route's DOM is committed and before paint, so the old
  // offset is never painted for a frame.
  useLayoutEffect(() => {
    const changed = lastPathname.current !== pathname;
    lastPathname.current = pathname;
    if (!changed || navigationType === 'POP' || hash || state?.preserveScroll) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navigationType, state]);

  return null;
}
